package co.diena.everything.app.worker.util

import co.diena.everything.api.Bucket
import co.diena.everything.api.EntityId
import co.diena.everything.api.EverythingApi
import co.touchlab.kermit.Logger
import com.github.michaelbull.result.Err
import com.github.michaelbull.result.Ok
import com.github.michaelbull.result.map
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.consumeAsFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Semaphore
import kotlinx.coroutines.sync.withPermit
import org.lortex.lines.common.AnyError
import org.lortex.lines.common.AnyResult

private class LoadBucketRequest(
    val bucket: BucketOptionalCategory,
    val work: suspend () -> Unit,
    var cancelled: Boolean = false,
)

data class BucketOptionalCategory(
    val bucket: Bucket,
    val category: EntityId?,
) : Comparable<BucketOptionalCategory> {
    override fun compareTo(other: BucketOptionalCategory): Int {
        val bucketCompare = bucket.compareTo(other.bucket)
        if (bucketCompare != 0) return bucketCompare
        return category?.compareTo(other.category ?: EntityId.parse("Q0"))
            ?: (other.category?.compareTo(EntityId.parse("Q0")) ?: 0)
    }
}

private val log = Logger.withTag("BucketLoader")

class BucketLoader(
    private val coroutineScope: CoroutineScope,
    private val concurrency: Int = 16,
    private val api: EverythingApi,
    private val limitPerBucket: Int = 10,
) : Loader<BucketOptionalCategory, List<EntityId>> {
    private val queue = Channel<LoadBucketRequest>(capacity = Int.MAX_VALUE)
    private val semaphore = Semaphore(concurrency)

    init {
        coroutineScope.launch {
            coroutineScope {
                try {
                    queue
                        .consumeAsFlow()
                        .collect { loadRequest ->
                            launch {
                                semaphore.withPermit {
                                    //   AppLogger.i("BucketLoader", "processing ${loadRequest.bucket}")
                                    if (!loadRequest.cancelled) {
                                        loadRequest.work()
                                    }
                                }
                            }
                        }
                } catch (e: CancellationException) {
                    log.e("queue cancelled: $e")
                }
            }
        }
    }

    /*
    Schedule the load request for this bucket. Cancel the request if it's not already being processed.
     */
    suspend fun loadEvents(
        key: BucketOptionalCategory,
    ): AnyResult<List<EntityId>> {
        val (bucket, category) = key
        val result = Channel<AnyResult<List<EntityId>>>()
        val request = LoadBucketRequest(
            bucket = key,
            work = {
                runCatching {
                    val events = Ok(
                        api
                            .loadEvents(listOf(bucket), category)
                            .take(limitPerBucket) // maybe move this server side.
                    )
                    /* .mapError {
                         AnyError(it).wrap("Failed to resolve bucket $key")
                     }*/
                    result.send(events.map { it.map { it.second } })
                }.exceptionOrNull()?.let {
                    if (it is CancellationException) {
                        result.cancel(it)
                    } else {
                        log.e("failed to load $bucket: $it")
                        result.send(Err(AnyError(it).wrap("Failed to load events for bucket $bucket")))
                    }
                }
            },
        )

        //  AppLogger.i("BucketLoader", "requesting $bucket")
        queue.send(request)

        return try {
            result.receive().also {
                //   AppLogger.i("BucketLoader", "received $bucket -> ${it.get()?.size}")
            }
        } catch (e: CancellationException) {
            //   AppLogger.i("BucketLoader", "request for $bucket cancelled: $e")
            result.cancel()
            request.cancelled = true
            throw e
        }
    }

    override suspend fun load(key: BucketOptionalCategory): AnyResult<List<EntityId>> {
        return loadEvents(key)
    }
}